import { useEffect } from 'react'
import { MathUtils } from 'three'
import { useCameraStore } from '../stores/useCameraStore'
import { usePOIHoverStore } from '../stores/usePOIHoverStore'
import { useLoadingStore } from '../stores/useLoadingStore'
import { POI } from '../config/cameraConfig'

function POICollider({ poi }: { poi: POI }) {
  const setHoveredPOIId = usePOIHoverStore((s) => s.setHoveredPOIId)
  const showColliderWireframes = usePOIHoverStore((s) => s.showColliderWireframes)
  const colliderOverride = usePOIHoverStore((s) => s.colliderOverrides[poi.id])
  const getColliderForPOI = usePOIHoverStore((s) => s.getColliderForPOI)
  const collider = colliderOverride ?? getColliderForPOI(poi)

  const posX = poi.target.x + collider.offset.x
  const posY = poi.target.y + collider.offset.y
  const posZ = poi.target.z + collider.offset.z

  return (
    <mesh
      position={[posX, posY, posZ]}
      rotation={[0, MathUtils.degToRad(collider.rotation ?? 0), 0]}
      onPointerOver={(e) => {
        e.stopPropagation()
        setHoveredPOIId(poi.id)
        document.body.style.cursor = 'pointer'
      }}
      onPointerOut={(e) => {
        e.stopPropagation()
        setHoveredPOIId(null)
        document.body.style.cursor = 'auto'
      }}
      onClick={(e) => {
        e.stopPropagation()
        const store = useCameraStore.getState()
        if (store.currentPOI?.id === poi.id) {
          store.incrementTransitionTrigger()
        } else {
          store.setCurrentPOI(poi)
        }
      }}
    >
      <boxGeometry args={[collider.size.x, collider.size.y, collider.size.z]} />
      {showColliderWireframes ? (
        <meshBasicMaterial color="#00ffff" wireframe transparent opacity={0.3} />
      ) : (
        <meshBasicMaterial transparent opacity={0} />
      )}
    </mesh>
  )
}

export default function POIColliders() {
  const pois = useCameraStore((s) => s.pois)
  const isLoadingPOIs = useCameraStore((s) => s.isLoadingPOIs)
  const isLoadingVisible = useLoadingStore((s) => s.isVisible)
  const currentPOI = useCameraStore((s) => s.currentPOI)

  // Clear hover when camera transitions to a POI
  useEffect(() => {
    usePOIHoverStore.getState().setHoveredPOIId(null)
  }, [currentPOI])

  if (isLoadingPOIs || pois.length === 0 || isLoadingVisible) {
    return null
  }

  return (
    <>
      {pois.map((poi) => (
        <POICollider key={poi.id} poi={poi} />
      ))}
    </>
  )
}
